import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilterList,
  faRotate,
} from "@fortawesome/pro-regular-svg-icons";
import { useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";
import useExporter from "../../hooks/useExporter";
import PriceFormatter from "../../helpers/PriceFormater";
import { getDomains, updateBillingGroupOnDomains } from "../../api/Agreement";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import Modal from "../../components/Modal";
import Dropdown from "../../components/Dropdown";

const DomainsPage = () => {
  const { handleExport } = useExporter();
  const { agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const [domains, setDomains] = useState([]);

  const [selectedDomains, setSelectedDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [billingGroups, setBillingGroups] = useState([]);
  const [selectedBillingGroup, setSelectedBillingGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  useEffect(() => {
    handleGetDomainsByAgreement();
  }, []);

  useEffect(() => {
    const fetchBillingGroups = async () => {
      try {
        const accessToken = await getAccessToken();
        const groups = await getBillingGroupsByAgreement(
          agreementPublicId,
          accessToken
        );
        setBillingGroups(groups);
      } catch (error) {
        console.error("Failed to fetch billing groups:", error);
      }
    };

    fetchBillingGroups();
  }, [agreementPublicId, getAccessToken]);

  const handleExportToExcel = () => {
    const domainsToExport =
      selectedDomains.length > 0
        ? filteredDomains.filter((domain) =>
            selectedDomains.includes(domain.nameSrsItemId)
          )
        : filteredDomains;

    if (domainsToExport.length === 0) {
      toast.error("No domains selected");
      return;
    }

    const domainsData = domainsToExport.map(
      ({
        nameSrsItemId,
        domainName,
        price,
        total,
        currency,
        renewalDate,
        autoRenew,
      }) => ({
        "Domain ID": nameSrsItemId,
        Name: domainName,
        Price: price,
        "Total ex. VAT": total,
        Currency: currency,
        "Renewal date": renewalDate,
        "Auto-renewal": autoRenew,
      })
    );
    console.log("Exporting Domain data:", domainsData);

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Domain_${currentDate}`;

    handleExport({ Domain: domainsData }, filename);
  };

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = domains;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = domains.filter(
          (domain) =>
            domain.domainName?.toLowerCase().includes(searchLower) ||
            domain.price?.toString().toLowerCase().includes(searchLower) ||
            domain.billingGroupName?.toString().toLowerCase().includes(searchLower) ||
            domain.updatedBy?.toLowerCase().includes(searchLower) ||
            domain.createdBy?.toLowerCase().includes(searchLower)
        );
      }

      const sorted = [...filtered].sort((a, b) => {
        const aValue = a[sortColumn]?.toString().toLowerCase();
        const bValue = b[sortColumn]?.toString().toLowerCase();
        return sortOrder === "asc"
          ? aValue > bValue
            ? 1
            : -1
          : aValue < bValue
          ? 1
          : -1;
      });

      setFilteredDomains(sorted);
    };

    applyFilterAndSort();
  }, [search, domains, sortColumn, sortOrder]);

  const handleGetDomainsByAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const data = await getDomains(agreementPublicId, accessToken);

      data.forEach((domain) => {
        domain.friendlyPrice = PriceFormatter(domain.price);
        domain.autoRenew = domain.autoRenew ? "Yes" : "No";
      });

      setDomains(data);
      setFilteredDomains(data);
      setDomainsLoaded(true);
    } catch (error) {
      console.error(error);
      setDomainsLoaded(true);
    }
  };

  const handleSyncDomains = async () => {
    const toastId = toast.loading("Starting domain sync...", {
      iconTheme: {
        primary: "#2196F3",
        secondary: "#E3F2FD",
      },
    });

    try {
      const baseUrl = process.env.REACT_APP_DIFLEX_DOMAINSYNC_URL;

      const url = `${baseUrl}&agreementPublicId=${encodeURIComponent(
        agreementPublicId
      )}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

      toast.success("Domains successfully synced!", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
      handleGetDomainsByAgreement();
    } catch (err) {
      console.error("Failed to trigger domain sync:", err);
      toast.error("Failed to start domain sync.", { id: toastId });
    }
  };

  const handleOpenModal = () => {
    if (selectedDomains.length === 0) {
      toast.error("No Domains selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleChangeBillingGroup = async () => {
  if (!selectedBillingGroup || selectedDomains.length === 0) {
    toast.error("Please select Domains and a billing group.");
    return;
  }

  try {
    const accessToken = await getAccessToken();

    await updateBillingGroupOnDomains(
      selectedDomains,
      selectedBillingGroup,
      accessToken
    );

    toast.success("Billing group updated successfully for selected Domains!", {
      iconTheme: {
        primary: "#4CAF50",
        secondary: "#DFF2BF",
      },
    });

    window.dispatchEvent(new Event("domains:updated"));
    await handleGetDomainsByAgreement();

    setSelectedDomains([]);
    setIsModalOpen(false);
  } catch (error) {
    console.error("Failed to update billing group:", error);
    toast.error("Failed to update billing group for selected Domains.");
  }
};


  const handleSelectBillingGroup = (name) => {
    const selected = billingGroups.find((g) => g.name === name);
    if (selected) {
      setSelectedBillingGroup(selected.billingGroupPublicId);
    }
  };

  console.log("Filtered Domains:", filteredDomains);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Domains"} loading={!domainsLoaded} />
            </div>

            <div className={`${!domainsLoaded ? "loading" : ""} page-content`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Domains</h2>
                 <button
                      className="btn-primary-small"
                      onClick={handleSyncDomains}
                    >
                      <FontAwesomeIcon
                        icon={faRotate}
                        className="mr-2 text-lg"
                      />
                      Sync domains
                    </button>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                    <button
                      className="btn btn-outline-primary-small"
                      onClick={handleOpenModal}
                      disabled={selectedDomains.length === 0}
                    >
                      Update billing group
                    </button>             

                  {/* <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div> */}

                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"name, domain"}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                    <button
                      className="btn-outline-primary-small ml-4"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredDomains}
                    selectedRows={selectedDomains}
                    setSelectedRows={setSelectedDomains}
                    noEdit={true}
                    disableLink={true}
                    noDelete={true}
                    columns={[
                      {
                        key: "nameSrsItemId",
                        label: "Domain ID",
                      },
                      { key: "domainName", label: "Domain name" },
                      {
                        key: "billingGroupName",
                        label: "Billing Group",
                      },
                      {
                        key: "price",
                        label: "Monthly price",
                      },
                      {
                        key: "total",
                        label: "Total ex. VAT",
                      },
                      {
                        key: "currency",
                        label: "Currency",
                      },
                      {
                        key: "renewalDate",
                        label: "Renewal date",
                      },
                      {
                        key: "autoRenew",
                        label: "Auto-renewal",
                      },
                    ]}
                    primaryKey="nameSrsItemId"
                    basePath={`/${agreementPublicId}/domains/edit/`}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">Change Billing Group</h2>

          <Dropdown
            label="Select Billing Group"
            options={billingGroups.map((group) => group.name)}
            value={
              billingGroups.find(
                (group) => group.billingGroupPublicId === selectedBillingGroup
              )?.name || ""
            }
            onSelect={handleSelectBillingGroup}
          />

          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleChangeBillingGroup}>
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default DomainsPage;
